import { useCallback, useEffect, useMemo, useState } from "react";

import { Client } from "@/Common/Api/Client";
import { ClientResponse } from "@/Common/Api/ClientResponse";
import { Icon } from "@/Common/Components/IconControls/IconControls";
import { PasswordConfirmationPopup } from "@/Common/Components/Modals/PasswordConfirmationPopup";
import { NewFlowMultifactorAuthenticationSetupPopup } from "@/Common/Components/MultifactorAuthentication/NewFlowMultifactorAuthenticationSetupPopup";
import { useMount } from "@/Common/CustomHooks/useMount";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { EnumHelper } from "@/Common/Helpers/EnumHelper";
import { AuthenticatorCodeDeliveryMethod } from "@/Common/Models/MultifactorAuthentication/AuthenticatorCodeDeliveryMethod";
import { IMultifactorAuthenticationUserSettings } from "@/Common/Models/UI/IMultifactorAuthenticationUserSettings";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { LinkButton } from "@/DesignSystem";

interface INewFlowMultifactorAuthenticationSettingsProps {
    emails: string[],
    lastVerifiedEmail: string | null;
    onRequireEmailValidation: () => void;
}

class NewFlowMultifactorAuthenticationSettingsUtils {
    /**
     * Fetch the authentication info for the current user.
     */
    public static async fetchCurrentUserSettings(): Promise<IMultifactorAuthenticationUserSettings> {
        const response = await Client.postAsync<IMultifactorAuthenticationUserSettings>(
            ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_GetSettings,
            {}
        );

        return response.isSuccess
            ? this.parseSettingsResponse(response)
            : null;
    }

    /**
     * Save the authentication code delivery method for the current user.
     */
    public static async saveCodeDeliveryMethod(codeDeliveryMethod: AuthenticatorCodeDeliveryMethod): Promise<IMultifactorAuthenticationUserSettings> {
        const response = await Client.postAsync<IMultifactorAuthenticationUserSettings>(
            ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_SaveCodeDeliveryMethod,
            { CodeDeliveryMethod: codeDeliveryMethod }
        );

        return response.isSuccess
            ? this.parseSettingsResponse(response)
            : null;
    }

    /**
     * Enable the authenticator for the current user.
     */
    public static async activate(
        setupToken: string,
        authenticationCode: string,
        codeDeliveryMethod: AuthenticatorCodeDeliveryMethod,
        password: string
    ): Promise<{ response: ClientResponse<any>, settings?: IMultifactorAuthenticationUserSettings }> {
        const response = await Client.postAsync<IMultifactorAuthenticationUserSettings>(
            ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_Activate,
            {
                AuthenticationCode: authenticationCode,
                SetupToken: setupToken,
                CodeDeliveryMethod: codeDeliveryMethod,
                Password: password
            }
        );

        return response.isSuccess
            ? { response, settings: this.parseSettingsResponse(response) }
            : { response };
    }

    /**
     * Deactivate the authentication for the current user.
     */
    public static async deactivate(password: string): Promise<{
        response: ClientResponse<IMultifactorAuthenticationUserSettings>,
        settings?: IMultifactorAuthenticationUserSettings
    }> {
        const response = await Client.postAsync<IMultifactorAuthenticationUserSettings>(
            ServerOperations.Server.Myle_MultifactorAuthenticationModule_CurrentUser_Deactivate,
            { Password: password }
        );

        return response.isSuccess
            ? { response, settings: this.parseSettingsResponse(response) }
            : { response };
    }

    private static parseSettingsResponse(response: ClientResponse<IMultifactorAuthenticationUserSettings>): IMultifactorAuthenticationUserSettings {
        const codeDeliveryMethod = EnumHelper.getValue(AuthenticatorCodeDeliveryMethod, response.data.CodeDeliveryMethod);

        // hack: set the store for backwards compatibility. Remove when the legacy controls are removed
        settingsStore.configuration = settingsStore.configuration.set("SelectedAuthenticatorCodeDeliveryMethod", codeDeliveryMethod);

        return {
            IsEnabled: response.data.IsEnabled,
            CodeDeliveryMethod: codeDeliveryMethod
        };
    }
}

function NewFlowMultifactorAuthenticationSettings(props: Readonly<INewFlowMultifactorAuthenticationSettingsProps>): JSX.Element {
    const { getText } = useTranslation();

    const [authenticationSettings, setAuthenticationSettings] = useState<IMultifactorAuthenticationUserSettings>({ IsEnabled: false, CodeDeliveryMethod: AuthenticatorCodeDeliveryMethod.NotSelected });
    const [authenticationSetupInfo, setAuthenticationSetupInfo] = useState<{ setupToken: string, authenticationCode: string, codeDeliveryMethod: AuthenticatorCodeDeliveryMethod }>();
    const [waitingForEmailValidation, setWaitingForEmailValidation] = useState(false);

    const [popupState, setPopupState] = useState({
        showConfigurationPopup: false,
        showActivationConfirmationPopup: false,
        showDeactivationConfirmationPopup: false
    });

    useMount(async () => {
        const authenticationSettings = await NewFlowMultifactorAuthenticationSettingsUtils.fetchCurrentUserSettings();
        setAuthenticationSettings(authenticationSettings);
    });

    const lastVerifiedEmail = useMemo(
        () => props.emails.firstOrDefault(email => email === props.lastVerifiedEmail),
        [props.emails, props.lastVerifiedEmail]
    );

    const isMultifactorAuthenticationByEmailAvailable = lastVerifiedEmail && cacheStore.ClinicSettings.EnableMultifactorAuthenticationByEmail;

    function closeAllPopups(reloadEmails: boolean = false): void {
        setPopupState({
            showConfigurationPopup: false,
            showActivationConfirmationPopup: false,
            showDeactivationConfirmationPopup: false
        });

        if (reloadEmails)
            sessionStore.dispatch(LocalOperations.Settings.LoadEmployeeEmails);
    }

    async function onCodeDeliveryMethodChange(codeDeliveryMethod: AuthenticatorCodeDeliveryMethod): Promise<void> {
        setAuthenticationSettings(authenticationSettings => ({ ...authenticationSettings, CodeDeliveryMethod: codeDeliveryMethod }));

        const authenticationSettings = await NewFlowMultifactorAuthenticationSettingsUtils.saveCodeDeliveryMethod(codeDeliveryMethod);
        setAuthenticationSettings(authenticationSettings);
    }

    const onModifyLinkClicked = useCallback(
        async () => {
            if (authenticationSettings.IsEnabled) {
                setPopupState(popupState => ({ ...popupState, showConfigurationPopup: true }));
                return;
            }

            if (!lastVerifiedEmail) {
                setWaitingForEmailValidation(true);
                props.onRequireEmailValidation();
                return;
            }

            setPopupState(popupState => ({ ...popupState, showConfigurationPopup: true }));
        },
        [props, lastVerifiedEmail, authenticationSettings.IsEnabled]
    );

    useEffect(
        () => {
            if (lastVerifiedEmail && waitingForEmailValidation) {
                setWaitingForEmailValidation(false);
                onModifyLinkClicked();
            }
        },
        [lastVerifiedEmail, waitingForEmailValidation, onModifyLinkClicked]
    );

    async function onSaveAuthentication(setupToken: string, authenticationCode: string, codeDeliveryMethod: AuthenticatorCodeDeliveryMethod): Promise<void> {
        setAuthenticationSetupInfo({ setupToken, authenticationCode, codeDeliveryMethod });

        setPopupState({
            showConfigurationPopup: true,
            showActivationConfirmationPopup: true,
            showDeactivationConfirmationPopup: false
        });
    }

    async function onActivateConfirm(password: string): Promise<ClientResponse<IMultifactorAuthenticationUserSettings>> {
        const { response, settings } = await NewFlowMultifactorAuthenticationSettingsUtils.activate(
            authenticationSetupInfo.setupToken,
            authenticationSetupInfo.authenticationCode,
            authenticationSetupInfo.codeDeliveryMethod,
            password
        );

        if (settings)
            setAuthenticationSettings(settings);

        if (response.isSuccess)
            closeAllPopups();

        return response;
    }

    async function onDeactivateConfirm(password: string): Promise<ClientResponse<IMultifactorAuthenticationUserSettings>> {
        const { response, settings } = await NewFlowMultifactorAuthenticationSettingsUtils.deactivate(password);

        if (settings)
            setAuthenticationSettings(settings);

        if (response.isSuccess)
            closeAllPopups();

        return response;
    }

    function renderAuthenticationDeactivated(): JSX.Element {
        return <label className="invalid" data-cy="multifactor-authentication-settings-deactivated">{getText("MultifactorAuthentication_NotConfiguredLabel")}</label>;
    }

    function renderAuthenticationActivated(): JSX.Element {
        return (
            <div data-cy="multifactor-authentication-settings-activated">
                <Icon type="checkmark" color="green" />
                <label className="margin-xs-left">{getText("MultifactorAuthentication_ActivatedLabel")}</label>
                <div>
                    <span className="margin-xs-left">{getText("MultifactorAuthentication_ThirdPartyAuthenticatorAppLabel")}</span>
                </div>
            </div>
        );
    }

    function renderAuthenticationDeliveryMethodSelection(): JSX.Element {
        return (
            <div data-cy="multifactor-authentication-settings-activated-email">
                <Icon type="checkmark" color="green" />
                <label className="margin-xs-left">{getText("MultifactorAuthentication_ActivatedLabel")}</label>
                <div>
                    <label>
                        <input
                            type="radio"
                            name="multifactor-authentication-settings-delivery-method"
                            value={EnumHelper.getName(AuthenticatorCodeDeliveryMethod, AuthenticatorCodeDeliveryMethod.Application)}
                            checked={authenticationSettings.CodeDeliveryMethod !== AuthenticatorCodeDeliveryMethod.Email}
                            onChange={e => onCodeDeliveryMethodChange(EnumHelper.getValue(AuthenticatorCodeDeliveryMethod, e.target.value))}
                            data-cy="multifactor-authentication-settings-application"
                        />
                        <span className="margin-xs-left">{getText("MultifactorAuthentication_ThirdPartyAuthenticatorAppLabel")}</span>
                        <span className="bold"> {getText("MultifactorAuthentication_Recommended")}</span>
                    </label>
                </div>
                <div>
                    <label>
                        <input
                            type="radio"
                            name="multifactor-authentication-settings-delivery-method"
                            value={EnumHelper.getName(AuthenticatorCodeDeliveryMethod, AuthenticatorCodeDeliveryMethod.Email)}
                            checked={authenticationSettings.CodeDeliveryMethod === AuthenticatorCodeDeliveryMethod.Email}
                            onChange={e => onCodeDeliveryMethodChange(EnumHelper.getValue(AuthenticatorCodeDeliveryMethod, e.target.value))}
                            data-cy="multifactor-authentication-settings-email"
                        />
                        <span className="margin-xs-left">{getText("EmailLabel")}</span>
                    </label>
                </div>
            </div>
        );
    }

    let renderAithenticatorInfo: () => JSX.Element;

    if (authenticationSettings.IsEnabled) {
        renderAithenticatorInfo = isMultifactorAuthenticationByEmailAvailable
            ? renderAuthenticationDeliveryMethodSelection
            : renderAuthenticationActivated;
    }
    else {
        renderAithenticatorInfo = renderAuthenticationDeactivated;
    }

    const showDeactivate = !cacheStore.ClinicSettings.EnableMandatoryMultifactorAuthentication
        && authenticationSettings.IsEnabled;

    return (
        <div className="row multifactor-authentication-row">
            <div className="hidden-sm hidden-xs col-md-2 col-lg-2 text-right">
                <label className="form-label bold">{getText("MultifactorAuthenticationLabel")}</label>
            </div>
            <div className="col-sm-6 col-md-4 col-lg-4">
                {renderAithenticatorInfo()}
                {popupState.showConfigurationPopup && (
                    <NewFlowMultifactorAuthenticationSetupPopup
                        authenticationSettings={authenticationSettings}
                        lastVerifiedEmail={props.lastVerifiedEmail}
                        onSave={(setupToken, authenticationCode, codeDeliveryMethod) => onSaveAuthentication(setupToken, authenticationCode, codeDeliveryMethod)}
                        onCancel={() => closeAllPopups()}
                    />
                )}
                {popupState.showActivationConfirmationPopup && (
                    <PasswordConfirmationPopup
                        message={getText("MultifactorAuthentication_ConfirmActivationMessage")}
                        clearOnError
                        onConfirm={password => onActivateConfirm(password)}
                        onCancel={() => setPopupState(popupState => ({ ...popupState, showActivationConfirmationPopup: false }))}
                    />
                )}
                {popupState.showDeactivationConfirmationPopup && (
                    <PasswordConfirmationPopup
                        title={getText("MultifactorAuthentication_DeactivateMultifactorAuthenticationLabel")}
                        message={getText("MultifactorAuthentication_DeactivateMultifactorAuthenticationSettingsMessage")}
                        clearOnError
                        onConfirm={password => onDeactivateConfirm(password)}
                        onCancel={() => closeAllPopups()}
                    />
                )}
            </div>
            <div className="col-sm-4 col-md-6 col-lg-6">
                <LinkButton
                    label={getText("ModifyLabel")}
                    onClick={() => onModifyLinkClicked()}
                    underline={false}
                    variant="brand"
                    dataCy="multifactor-authentication-settings-modify"
                />
                {showDeactivate && (
                    <>
                        <span className="padding-xs">|</span>
                        <LinkButton
                            label={getText("DeactivateLabel")}
                            onClick={() => setPopupState(popupState => ({ ...popupState, showDeactivationConfirmationPopup: true }))}
                            underline={false}
                            variant="error"
                            dataCy="multifactor-authentication-settings-deactivate"
                        />
                    </>
                )}
            </div>
        </div>
    );
}

export { NewFlowMultifactorAuthenticationSettings, NewFlowMultifactorAuthenticationSettingsUtils };
